import React, { useState, useRef } from "react";

interface FileUploadProps {
  label: string;
  required?: boolean;
  onFileSelect: (
    file: File,
    presignedUrl: string,
    s3Url: string,
  ) => Promise<void>;
  onRemove?: () => void;
  currentFile?: string; // S3 URL of existing file
  acceptedFileTypes?: string[]; // e.g., ['.pdf', '.jpg', '.png']
  maxFileSize?: number; // bytes (default 10MB)
  disabled?: boolean;
  className?: string;
  error?: string;
}

/**
 * FileUpload — Drag-and-drop file upload with S3 integration.
 *
 * Features:
 * - Drag-and-drop + click-to-browse
 * - File type validation
 * - File size validation
 * - Upload progress (loading spinner)
 * - Error handling
 * - Display existing file with download link
 * - Remove file option
 * - Accessibility: ARIA labels, semantic HTML
 *
 * Usage:
 * ```tsx
 * <FileUpload
 *   label="Receipt Document"
 *   required
 *   onFileSelect={async (file, presignedUrl, s3Url) => {
 *     // presignedUrl is obtained from backend
 *     // s3Url is the final S3 object URL
 *     handleFieldChange("receiptUrl", s3Url);
 *   }}
 *   onRemove={() => handleFieldChange("receiptUrl", null)}
 *   currentFile={formData.receiptUrl}
 *   acceptedFileTypes={['.pdf', '.jpg', '.png']}
 *   maxFileSize={10 * 1024 * 1024} // 10MB
 * />
 * ```
 */
const FileUpload: React.FC<FileUploadProps> = ({
  label,
  required = false,
  onFileSelect,
  onRemove,
  currentFile,
  acceptedFileTypes = [".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx"],
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  disabled = false,
  className = "",
  error,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<{
    name: string;
    size: number;
    url: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get file extension
  const getFileExtension = (fileName: string): string => {
    return "." + fileName.split(".").pop()?.toLowerCase();
  };

  // Validate file
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const extension = getFileExtension(file.name);

    if (!acceptedFileTypes.includes(extension)) {
      return {
        valid: false,
        error: `File type not allowed. Accepted: ${acceptedFileTypes.join(
          ", ",
        )}`,
      };
    }

    if (file.size > maxFileSize) {
      const maxMB = (maxFileSize / (1024 * 1024)).toFixed(1);
      return {
        valid: false,
        error: `File too large. Maximum size: ${maxMB}MB`,
      };
    }

    return { valid: true };
  };

  // Handle file selection (from input or drag-drop)
  const handleFileSelect = async (file: File) => {
    setUploadError(null);

    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadError(validation.error || "Invalid file");
      return;
    }

    try {
      setIsUploading(true);

      // Call parent's onFileSelect which should:
      // 1. Request pre-signed URL from backend
      // 2. Upload file to S3
      // 3. Return S3 URL
      //
      // This is implemented in the parent form component
      await onFileSelect(file, "", ""); // presignedUrl and s3Url passed by parent

      // Store file metadata for display
      setUploadedFile({
        name: file.name,
        size: file.size,
        url: "", // Will be set by parent
      });
    } catch (err: any) {
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    setUploadedFile(null);
    setUploadError(null);
    if (onRemove) {
      onRemove();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // Get file extension for icon
  const getFileIcon = (fileName: string): string => {
    const ext = getFileExtension(fileName).toLowerCase();
    if ([".jpg", ".jpeg", ".png", ".gif"].includes(ext)) return "🖼️";
    if ([".pdf"].includes(ext)) return "📄";
    if ([".doc", ".docx"].includes(ext)) return "📝";
    return "📎";
  };

  const displayFile =
    uploadedFile ||
    (currentFile ? { name: "Uploaded file", size: 0, url: currentFile } : null);

  return (
    <div className={`w-full ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Upload area or file display */}
      {!displayFile ? (
        <>
          {/* Drag-drop zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragging
                ? "border-primary-500 bg-primary-50"
                : error
                ? "border-red-300 bg-red-50"
                : "border-gray-300 bg-gray-50"
            } ${
              disabled
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer hover:border-primary-500 hover:bg-primary-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleInputChange}
              disabled={disabled || isUploading}
              className="hidden"
              accept={acceptedFileTypes.join(",")}
              aria-label={label}
            />

            {isUploading ? (
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full mb-2"></div>
                <p className="text-sm text-gray-600">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <svg
                  className="h-8 w-8 text-gray-400 mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                  />
                </svg>
                <p className="text-sm font-medium text-gray-700">
                  Drag and drop your file here
                </p>
                <p className="text-xs text-gray-500 mt-1">or</p>
                <button
                  type="button"
                  onClick={handleBrowseClick}
                  disabled={disabled || isUploading}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Click to browse
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Accepted: {acceptedFileTypes.join(", ")} • Max{" "}
                  {(maxFileSize / (1024 * 1024)).toFixed(0)}MB
                </p>
              </div>
            )}
          </div>

          {/* Upload error */}
          {uploadError && (
            <p className="text-red-600 text-xs mt-1">{uploadError}</p>
          )}
          {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
        </>
      ) : (
        <>
          {/* Uploaded file display */}
          <div className="border border-green-300 bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">
                  {getFileIcon(displayFile.name)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {displayFile.name}
                  </p>
                  {displayFile.size > 0 && (
                    <p className="text-xs text-gray-500">
                      {formatFileSize(displayFile.size)}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2 ml-2">
                {/* Download link */}
                {displayFile.url && (
                  <a
                    href={displayFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded"
                    title="Download file"
                    aria-label="Download file"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  </a>
                )}

                {/* Remove button */}
                <button
                  type="button"
                  onClick={handleRemove}
                  disabled={disabled || isUploading}
                  className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Remove file"
                  aria-label="Remove file"
                >
                  <svg
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Replace file button */}
            <button
              type="button"
              onClick={handleBrowseClick}
              disabled={disabled || isUploading}
              className="mt-3 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Choose a different file
            </button>
          </div>

          {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
        </>
      )}
    </div>
  );
};

export default FileUpload;
